/**
 * React hook for the Tauri/Keycloak OIDC flow (MOD-885 tranche 4).
 *
 * Thin wrapper over the `oidc-client-ts` UserManager — subscribes to
 * user-loaded / user-unloaded / silent-renew-error events so React
 * components re-render when the session state changes (e.g. after a
 * background refresh).
 *
 * Web consumers should use `useSession()` from `next-auth/react`
 * instead; this hook is Tauri-only.
 */
import type { User } from 'oidc-client-ts';
import { useEffect, useState } from 'react';

import { getKeycloakUserManager } from './oidcClient';

export type KeycloakTauriState =
  | { status: 'loading'; user: null; error: null }
  | { status: 'unauthenticated'; user: null; error: null }
  | { status: 'authenticated'; user: User; error: null }
  | { status: 'error'; user: null; error: string };

export function useKeycloakTauri(): KeycloakTauriState {
  const [state, setState] = useState<KeycloakTauriState>({
    status: 'loading',
    user: null,
    error: null,
  });

  useEffect(() => {
    const manager = getKeycloakUserManager();
    let cancelled = false;

    void manager
      .getUser()
      .then((user) => {
        if (cancelled) return;
        if (user && !user.expired) {
          setState({ status: 'authenticated', user, error: null });
        } else {
          setState({ status: 'unauthenticated', user: null, error: null });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          user: null,
          error: err instanceof Error ? err.message : String(err),
        });
      });

    const onUserLoaded = (user: User): void => {
      setState({ status: 'authenticated', user, error: null });
    };
    const onUserUnloaded = (): void => {
      setState({ status: 'unauthenticated', user: null, error: null });
    };
    const onSilentRenewError = (err: Error): void => {
      // Refresh failed — Keycloak revoked the session or the refresh
      // token expired. Mirror the NextAuth `RefreshAccessTokenError`
      // channel so the UI can sign the user out and redirect to login.
      //
      // Deliberately do NOT clear the stored user here. `signoutRedirect`
      // (invoked downstream by the forced-logout chain) captures the
      // stored user's `id_token` as the `id_token_hint` on the
      // end-session request; Keycloak requires that hint to end the
      // session silently, without which the webview lands on a
      // logout-confirmation page (dismissed with a live SSO cookie
      // intact — the zombie-SSO state). oidc-client-ts's signout flow
      // captures the hint and removes the user atomically, so the
      // storage cleanup still happens — just after the hint has been
      // consumed. If the signout chain never runs (e.g. handler
      // failure), the surviving expired user converges on the next
      // boot's renew via `invalid_grant` → definitive classification.
      if (cancelled) return;
      setState({ status: 'error', user: null, error: err.message });
    };

    manager.events.addUserLoaded(onUserLoaded);
    manager.events.addUserUnloaded(onUserUnloaded);
    manager.events.addSilentRenewError(onSilentRenewError);

    return () => {
      cancelled = true;
      manager.events.removeUserLoaded(onUserLoaded);
      manager.events.removeUserUnloaded(onUserUnloaded);
      manager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, []);

  return state;
}
